import { useEffect, useRef, useState } from 'react';
import { useWizardStore } from '../store/wizardStore';
import { StepShell } from '../components/StepShell';
import { Button } from '../components/ui/Button';
import { apiPreviewContract } from '../api/contract.api';
import type { ContractPreviewPages } from '../api/contract.api';

/** iframe с автоматической высотой по содержимому */
function AutoHeightIframe({ srcDoc, title }: { srcDoc: string; title: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  function handleLoad() {
    const frame = iframeRef.current;
    if (!frame?.contentDocument) return;
    const h = frame.contentDocument.documentElement.scrollHeight;
    frame.style.height = `${h}px`;
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcDoc}
      title={title}
      sandbox="allow-same-origin"
      scrolling="no"
      onLoad={handleLoad}
      className="w-full block"
      style={{ minHeight: '400px', height: '800px', border: 'none' }}
    />
  );
}

export function StepReview() {
  const state = useWizardStore();
  const goTo = useWizardStore((s) => s.goTo);
  const [confirmed, setConfirmed] = useState(false);

  const [preview, setPreview] = useState<ContractPreviewPages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiPreviewContract(state)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.variant]);

  const subtitle =
    state.variant === 'variant2'
      ? 'Договор купли-продажи окон и договор подряда на монтаж'
      : state.variant === 'variant3'
        ? 'Договор купли-продажи ворот'
        : 'Объединённый договор купли-продажи и монтажа окон';

  const docsWord =
    (preview?.pages.length ?? 1) > 1 ? 'всех договоров' : 'условиями договора';

  return (
    <StepShell
      title="Ознакомление с договорами"
      subtitle={subtitle}
      current="review"
      footer={
        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-5 h-5 rounded accent-primary cursor-pointer"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span className="text-sm text-slate-700">
              Я ознакомился(ась) с {docsWord} и согласен(на) с их содержанием
            </span>
          </label>
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => goTo('green')}>
              Назад
            </Button>
            <Button disabled={!confirmed} onClick={() => goTo('signatures')}>
              Перейти к подписанию →
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-6 max-w-4xl w-full">
        {loading && (
          <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
            Загрузка предпросмотра…
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-32 text-red-500 text-sm px-6 text-center">
            Не удалось загрузить предпросмотр: {error}
          </div>
        )}
        {!loading && !error && preview &&
          preview.pages.map((html: string, i: number) => (
            <div key={i} className="flex flex-col gap-2">
              {preview.pages.length > 1 && (
                <div className="flex items-center gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-sm font-medium text-slate-700">
                    {preview.labels[i]}
                  </span>
                </div>
              )}
              <div className="rounded-xl border overflow-hidden shadow-sm bg-white">
                <AutoHeightIframe
                  srcDoc={html}
                  title={preview.labels[i] ?? `Договор ${i + 1}`}
                />
              </div>
            </div>
          ))}
      </div>
    </StepShell>
  );
}
